// Status constants for applications
export const APP_STATUS = {
  DRAFT: '0',
  PENDING: '1', 
  APPROVED: '2',
  REJECTED: '3',
  RESUBMIT: '4',
  AMENDED: 'amended'
};

// Status constants for application workers
export const WORKER_STATUS = {
  PENDING: '0',
  APPROVED: '1',
  REJECTED: '2'
};

// Comprehensive status mapping for all possible formats
export const STATUS_MAPPING = {
  // Draft status variations
  DRAFT: {
    numeric: 0,
    stringNumeric: '0',
    text: 'Draft',
    textLower: 'draft',
    textUpper: 'DRAFT'
  },
  // Pending status variations
  PENDING: {
    numeric: 1,
    stringNumeric: '1',
    text: 'Pending',
    textLower: 'pending',
    textUpper: 'PENDING',
    aliases: [
      'submitted', 'Submitted', 'SUBMITTED',
      'under_review', 'under review', 'Under Review', 'UNDER_REVIEW',
      'partial', 'Partial', 'PARTIAL'
    ]
  },
  // Approved status variations
  APPROVED: {
    numeric: 2,
    stringNumeric: '2',
    text: 'Approved',
    textLower: 'approved',
    textUpper: 'APPROVED',
    aliases: ['approve', 'Approve', 'APPROVE']
  },
  // Rejected status variations
  REJECTED: {
    numeric: 3,
    stringNumeric: '3',
    text: 'Rejected',
    textLower: 'rejected',
    textUpper: 'REJECTED',
    aliases: ['reject', 'Reject', 'REJECT']
  },
  // Resubmit status variations
  RESUBMIT: {
    numeric: 4,
    stringNumeric: '4',
    text: 'Resubmit',
    textLower: 'resubmit',
    textUpper: 'RESUBMIT',
    aliases: ['resubmitted', 'Resubmitted', 'RESUBMITTED']
  },
  // Amended status (special case)
  AMENDED: {
    numeric: null, // No numeric equivalent
    stringNumeric: 'amended',
    text: 'Amended',
    textLower: 'amended',
    textUpper: 'AMENDED'
  }
};

// Status normalization utility
export const statusNormalizer = {
  // Normalize any status input to a standard numeric string format
  toNumericString: (status) => {
    if (status === null || status === undefined) return null;
    
    const statusStr = String(status).toLowerCase().trim();
    
    // Direct numeric string matches
    if (['0', '1', '2', '3', '4'].includes(statusStr)) {
      return statusStr;
    }
    
    // Text-based matches
    for (const [key, mapping] of Object.entries(STATUS_MAPPING)) {
      if (mapping.textLower === statusStr || 
          (mapping.aliases && mapping.aliases.some(alias => alias.toLowerCase() === statusStr))) {
        return mapping.stringNumeric;
      }
    }
    
    return null; // Unknown status
  },
  
  // Normalize to standard text format
  toText: (status) => {
    const normalized = statusNormalizer.toNumericString(status);
    if (!normalized) return 'Unknown';
    
    for (const [key, mapping] of Object.entries(STATUS_MAPPING)) {
      if (mapping.stringNumeric === normalized) {
        return mapping.text;
      }
    }
    
    return 'Unknown';
  },
  
  // Normalize to numeric format (for API calls)
  toNumeric: (status) => {
    const normalized = statusNormalizer.toNumericString(status);
    if (!normalized || normalized === 'amended') return null;
    return parseInt(normalized, 10);
  },
  
  // Check if two statuses are equivalent
  areEqual: (status1, status2) => {
    const norm1 = statusNormalizer.toNumericString(status1);
    const norm2 = statusNormalizer.toNumericString(status2);
    return norm1 === norm2 && norm1 !== null;
  }
};

// Enhanced status checking utility functions using normalization
export const statusHelpers = {
  isPending: (status) => {
    return statusNormalizer.toNumericString(status) === '1';
  },
  
  isApproved: (status) => {
    return statusNormalizer.toNumericString(status) === '2';
  },
  
  isRejected: (status) => {
    return statusNormalizer.toNumericString(status) === '3';
  },
  
  isResubmit: (status) => {
    return statusNormalizer.toNumericString(status) === '4';
  },
  
  isDraft: (status) => {
    return statusNormalizer.toNumericString(status) === '0';
  },
  
  isAmended: (status) => {
    return statusNormalizer.toNumericString(status) === 'amended';
  }
};

// Worker status helpers (for application workers)
export const workerStatusHelpers = {
  isPending: (status) => {
    return ['0', 'pending', 'selected'].includes(String(status).toLowerCase());
  },
  
  isApproved: (status) => {
    return ['1', 'approved'].includes(String(status).toLowerCase());
  },
  
  isRejected: (status) => {
    return ['2', 'rejected', 'released'].includes(String(status).toLowerCase());
  },

  isReleased: (status) => {
    return String(status).toLowerCase() === 'released';
  }
};

// Enhanced status text function using normalization
export function statusToText(status) {
  return statusNormalizer.toText(status);
}

// Worker status text function
export function workerStatusToText(status) {
  switch (String(status).toLowerCase()) {
    case '0':
    case 'pending':
    case 'selected':
      return 'Pending';
    case '1':
    case 'approved':
      return 'Approved';
    case 'released':
      return 'Released';
    case '2':
    case 'rejected':
      return 'Rejected';
    default:
      return 'Unknown';
  }
}

// Application status badge classes
export function getStatusBadgeClass(status) {
  if (statusHelpers.isPending(status)) {
    return 'bg-yellow-100 text-yellow-800';
  } else if (statusHelpers.isApproved(status)) {
    return 'bg-green-100 text-green-800';
  } else if (statusHelpers.isRejected(status)) {
    return 'bg-red-100 text-red-800';
  } else if (statusHelpers.isResubmit(status)) {
    return 'bg-orange-100 text-orange-800';
  } else if (statusHelpers.isDraft(status)) {
    return 'bg-gray-100 text-gray-800';
  }
  return 'bg-gray-100 text-gray-600';
}

// Worker status badge classes
export function getWorkerStatusBadgeClass(status) {
  if (workerStatusHelpers.isPending(status)) {
    return 'bg-yellow-100 text-yellow-800';
  } else if (workerStatusHelpers.isApproved(status)) {
    return 'bg-green-100 text-green-800';
  } else if (workerStatusHelpers.isReleased(status)) {
    return 'bg-blue-100 text-blue-800';
  } else if (workerStatusHelpers.isRejected(status)) {
    return 'bg-red-100 text-red-800';
  }
  return 'bg-gray-100 text-gray-600';
}

// API utility functions for status handling
export const statusApiUtils = {
  // Prepare status for API calls (converts to numeric)
  forApiCall: (status) => {
    const numeric = statusNormalizer.toNumeric(status);
    return numeric !== null ? numeric : status; // Return original if can't convert
  },
  
  // Prepare status for database storage (converts to string numeric)
  forDatabase: (status) => {
    const stringNumeric = statusNormalizer.toNumericString(status);
    return stringNumeric !== null ? stringNumeric : status; // Return original if can't convert
  },
  
  // Prepare status for display (converts to readable text)
  forDisplay: (status) => {
    return statusNormalizer.toText(status);
  },
  
  // Validate if status is valid
  isValid: (status) => {
    return statusNormalizer.toNumericString(status) !== null;
  },
  
  // Get all possible formats for a status
  getAllFormats: (status) => {
    const normalized = statusNormalizer.toNumericString(status);
    if (!normalized) return null;
    
    for (const [key, mapping] of Object.entries(STATUS_MAPPING)) {
      if (mapping.stringNumeric === normalized) {
        return {
          numeric: mapping.numeric,
          stringNumeric: mapping.stringNumeric,
          text: mapping.text,
          textLower: mapping.textLower,
          textUpper: mapping.textUpper,
          aliases: mapping.aliases || []
        };
      }
    }
    return null;
  }
};

// Usage examples and documentation
export const statusExamples = {
  // Example: Converting database varchar status to API numeric
  databaseToApi: (dbStatus) => {
    // Input: '1', 'Pending', 'pending', 'submitted'
    // Output: 1 (numeric for API)
    return statusApiUtils.forApiCall(dbStatus);
  },
  
  // Example: Converting any status format to display text
  anyToDisplay: (status) => {
    // Input: 1, '1', 'pending', 'Pending', 'PENDING'
    // Output: 'Pending'
    return statusApiUtils.forDisplay(status);
  },
  
  // Example: Checking if statuses are equivalent
  compareStatuses: (status1, status2) => {
    // Returns true if: (1, 'pending'), ('1', 'Pending'), etc.
    return statusNormalizer.areEqual(status1, status2);
  },
  
  // Example: Filtering data by status
  filterByStatus: (dataArray, targetStatus) => {
    const normalizedTarget = statusNormalizer.toNumericString(targetStatus);
    return dataArray.filter(item => 
      statusNormalizer.toNumericString(item.status) === normalizedTarget
    );
  }
};

/* 
 * USAGE DOCUMENTATION:
 * 
 * 1. For API calls expecting numeric status:
 *    const apiStatus = statusApiUtils.forApiCall(dbStatus);
 *    fetch('/api/applications', { 
 *      method: 'POST', 
 *      body: JSON.stringify({ status: apiStatus }) 
 *    });
 * 
 * 2. For database storage:
 *    const dbStatus = statusApiUtils.forDatabase(userInput);
 *    
 * 3. For display in UI:
 *    const displayText = statusApiUtils.forDisplay(anyStatusFormat);
 *    
 * 4. For validation:
 *    if (statusApiUtils.isValid(userInput)) {
 *      // Process valid status
 *    }
 * 
 * 5. For status comparison:
 *    if (statusNormalizer.areEqual(status1, status2)) {
 *      // Statuses are equivalent
 *    }
 */
